import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  NgZone,
  inject,
  PLATFORM_ID
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { fadeInOut } from '../../animations/animations';
import { NewsletterFormComponent } from '../../components/newsletter-form/newsletter-form.component';
import { ScrollToDirective } from '../../directives/scroll-to.directive';
import { CommunityNewsListComponent } from '../../components/community-news-list/community-news-list.component';
import { PlaylistGridComponent } from '../../components/playlist-grid/playlist-grid.component';
import { environment } from '../../../environments/environment';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/** Matches the key used in EventDetailComponent */
const PENDING_PAYMENT_KEY = 'pendingPaymentRedirect';

/** Throttles the Django warm-up ping to once per browser session */
const DJANGO_WARMED_KEY = 'djangoApiWarmed';

/** Payment gateway URL — keep in sync with event-detail.component.ts */
const PAYMENT_GATEWAY_URL = 'https://www.wuxa.africa/event/restless-affairs-4';

// ScrollTrigger touches `window`/`document` as soon as it's registered,
// so this must never run during SSR (app.config.server.ts) — only ever
// on the client, and only once regardless of how many times Home is
// constructed.
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
  // Mobile Safari/Chrome resize their viewport as the address bar
  // hides/shows on scroll, which would otherwise make ScrollTrigger
  // recalculate (and jump) the pinned hero mid-gesture.
  ScrollTrigger.config({ ignoreMobileResize: true });
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NewsletterFormComponent,
    ScrollToDirective,
    CommunityNewsListComponent,
    PlaylistGridComponent
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  animations: [fadeInOut]
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private zone = inject(NgZone);

  /** Whether we arrived here via the "subscribe before payment" flow */
  private pendingPaymentRedirect = false;

  // Hero elements the scroll-driven intro animates. Grabbed via
  // template refs rather than querySelector so this keeps working
  // however the template gets restructured later.
  @ViewChild('heroSection') private heroSectionRef?: ElementRef<HTMLElement>;
  @ViewChild('heroBg') private heroBgRef?: ElementRef<HTMLElement>;
  @ViewChild('heroTitle') private heroTitleRef?: ElementRef<HTMLElement>;
  @ViewChild('heroSubtitle') private heroSubtitleRef?: ElementRef<HTMLElement>;
  @ViewChild('heroCta') private heroCtaRef?: ElementRef<HTMLElement>;
  @ViewChild('heroTicket') private heroTicketRef?: ElementRef<HTMLElement>;

  // Manifesto rack — the pinned, horizontally-scrubbed section that
  // complements the hero's vertical letter reveal with a different
  // scroll-tied move (see setupManifestoScroll() below).
  @ViewChild('manifestoSection') private manifestoSectionRef?: ElementRef<HTMLElement>;
  @ViewChild('manifestoTrack') private manifestoTrackRef?: ElementRef<HTMLElement>;
  @ViewChild('manifestoWatermark') private manifestoWatermarkRef?: ElementRef<HTMLElement>;
  @ViewChild('manifestoDots') private manifestoDotsRef?: ElementRef<HTMLElement>;

  // "Connect With Us" panel — cursor-reactive glow + magnetic pull.
  // Complements the hero/manifesto's scroll-tied moves with a second
  // mechanic driven by real pointer position instead of scroll (see
  // setupMagneticSocial() below).
  @ViewChild('magneticZone') private magneticZoneRef?: ElementRef<HTMLElement>;
  @ViewChild('magneticCursor') private magneticCursorRef?: ElementRef<HTMLElement>;

  private heroScrollTrigger?: ScrollTrigger;
  private heroTimeline?: gsap.core.Timeline;
  private manifestoScrollTrigger?: ScrollTrigger;
  private manifestoTimeline?: gsap.core.Timeline;
  private manifestoDotEls: HTMLElement[] = [];
  private magneticCleanupFns: Array<() => void> = [];

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.warmUpDjangoAPI();

      this.pendingPaymentRedirect = sessionStorage.getItem(PENDING_PAYMENT_KEY) === 'true';

      // Plain "/" navigations (no fragment) are already scrolled to the
      // top by App's global NavigationEnd handler (see app.ts). Only the
      // fragment case — the "join the community" deep link into
      // #subscription-section — needs handling here, since it has to
      // poll for the target element rather than jump to a fixed offset.
      const fragment = this.route.snapshot.fragment;
      if (fragment) {
        this.scrollToFragment(fragment);
      }
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setupHeroScrollIntro();
      this.setupManifestoScroll();
      this.setupMagneticSocial();
    }
  }

  ngOnDestroy(): void {
    // Kill our own ScrollTrigger/timeline explicitly rather than relying
    // on ScrollTrigger.getAll(), so navigating away from "/" never
    // leaves a pin or a scroll listener bound to elements Angular is
    // about to destroy.
    this.heroScrollTrigger?.kill();
    this.heroTimeline?.kill();
    this.manifestoScrollTrigger?.kill();
    this.manifestoTimeline?.kill();
    this.magneticCleanupFns.forEach((cleanup) => cleanup());
    this.magneticCleanupFns = [];
  }

  /**
   * Builds the pinned, scroll-scrubbed hero intro: the headline resolves
   * letter by letter — out of a blur, back to normal tracking — and the
   * subtitle/CTAs only drop in once it has, all mapped directly to
   * scroll position (not a timer), so it runs exactly as fast as the
   * user scrolls and reverses cleanly if they scroll back up.
   *
   * Respects `prefers-reduced-motion`: reduced-motion users, and anyone
   * whose browser hasn't hydrated far enough to have the refs yet, get
   * the section in its final state via the `no-scroll-fx` CSS fallback
   * instead of the animation.
   */
  private setupHeroScrollIntro(): void {
    const section = this.heroSectionRef?.nativeElement;
    const bg = this.heroBgRef?.nativeElement;
    const title = this.heroTitleRef?.nativeElement;
    const subtitle = this.heroSubtitleRef?.nativeElement;
    const cta = this.heroCtaRef?.nativeElement;
    const ticket = this.heroTicketRef?.nativeElement;

    if (!section || !bg || !title || !subtitle || !cta || !ticket) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      section.classList.add('no-scroll-fx');
      return;
    }

    const letters = this.splitHeroTitle(title);
    if (!letters.length) {
      section.classList.add('no-scroll-fx');
      return;
    }

    const finalLetterSpacing = getComputedStyle(title).letterSpacing;

    // ScrollTrigger fires on every scroll tick — build and animate it
    // outside Angular's zone so it doesn't trigger change detection on
    // every frame.
    this.zone.runOutsideAngular(() => {
      gsap.set(bg, { scale: 1.35, opacity: 0.4 });
      gsap.set(letters, { opacity: 0, filter: 'blur(14px)', y: 18, scale: 1.15 });
      gsap.set(title, { letterSpacing: '0.3em' });
      gsap.set([subtitle, cta, ticket], { opacity: 0, y: 24 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: '+=125%',
          scrub: 0.4,
          pin: true,
          pinSpacing: true,
          anticipatePin: 1
        }
      });

      tl.to(title, { letterSpacing: finalLetterSpacing, duration: 1, ease: 'none' }, 0)
        .to(letters, {
          opacity: 1,
          filter: 'blur(0px)',
          y: 0,
          scale: 1,
          duration: 1,
          ease: 'none',
          stagger: { each: 0.045, from: 'start' }
        }, 0)
        .to(bg, { scale: 1, opacity: 0.85, duration: 1, ease: 'none' }, 0.1)
        .to([subtitle, cta, ticket], {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'none',
          stagger: 0.08
        }, 0.75);

      this.heroTimeline = tl;
      this.heroScrollTrigger = tl.scrollTrigger;
    });
  }

  /**
   * Splits the headline's text into one <span class="hero-letter"> per
   * character (spaces become fixed-width spacer spans so word gaps
   * survive the split) so GSAP can stagger and blur each letter
   * independently. The element keeps its original text as an
   * `aria-label` (set in the template) so screen readers still get
   * "Restless By Nature" as one phrase; the letter spans themselves are
   * marked `aria-hidden`.
   *
   * Runs only in the browser, after the view — and, on an SSR'd first
   * load, after hydration — has already painted the plain text, so
   * there's no flash of missing content for no-JS or slow-JS visitors.
   */
  private splitHeroTitle(titleEl: HTMLElement): HTMLElement[] {
    const text = titleEl.textContent ?? '';
    if (!text.trim()) {
      return [];
    }

    titleEl.textContent = '';

    const letters: HTMLElement[] = [];
    for (const char of text) {
      const span = document.createElement('span');
      span.setAttribute('aria-hidden', 'true');
      if (char === ' ') {
        span.className = 'hero-letter hero-letter--space';
        span.textContent = '\u00A0';
      } else {
        span.className = 'hero-letter';
        span.textContent = char;
      }
      titleEl.appendChild(span);
      letters.push(span);
    }
    return letters;
  }

  /**
   * Builds the manifesto rack: the section pins in place while the
   * card track is translated horizontally, mapped directly to scroll
   * position (same `scrub` approach as the hero, no timers). A faint
   * "MANIFESTO" watermark behind the cards moves at a slower rate for
   * a parallax layer, and a dot trail below tracks progress through
   * the cards as they pass.
   *
   * Degrades the same way the hero does: `prefers-reduced-motion`, or
   * missing refs, leaves the section as it renders by default — a
   * plain horizontally-scrollable, scroll-snapped row (see the SCSS),
   * so the content stays fully reachable either way.
   */
  private setupManifestoScroll(): void {
    const section = this.manifestoSectionRef?.nativeElement;
    const track = this.manifestoTrackRef?.nativeElement;
    const watermark = this.manifestoWatermarkRef?.nativeElement;
    const dotsContainer = this.manifestoDotsRef?.nativeElement;

    if (!section || !track || !dotsContainer) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return;
    }

    this.manifestoDotEls = Array.from(dotsContainer.children) as HTMLElement[];
    if (!this.manifestoDotEls.length) {
      return;
    }

    // How far the track has to travel to bring its last card fully
    // into view. Recomputed on every ScrollTrigger refresh (via
    // invalidateOnRefresh) rather than measured once, so a resize or
    // a late font swap that changes card widths doesn't leave the
    // rack over- or under-shooting its own last card.
    const travelDistance = () => Math.max(track.scrollWidth - section.clientWidth, 0);

    this.zone.runOutsideAngular(() => {
      section.classList.add('js-pin');

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: () => '+=' + travelDistance(),
          scrub: 0.5,
          pin: true,
          pinSpacing: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => this.updateManifestoDots(self.progress)
        }
      });

      tl.to(track, { x: () => -travelDistance(), ease: 'none' }, 0);
      if (watermark) {
        tl.to(watermark, { x: () => -travelDistance() * 0.35, ease: 'none' }, 0);
      }

      this.manifestoTimeline = tl;
      this.manifestoScrollTrigger = tl.scrollTrigger;
    });
  }

  /** Lights up the dot matching how far through the manifesto rack the scroll has scrubbed. */
  private updateManifestoDots(progress: number): void {
    const lastIndex = this.manifestoDotEls.length - 1;
    const activeIndex = Math.round(progress * lastIndex);
    this.manifestoDotEls.forEach((dot, i) => dot.classList.toggle('active', i === activeIndex));
  }

  /**
   * Builds the "Connect With Us" panel's cursor-reactive glow and
   * magnetic pull — a second animation mechanic (real pointer
   * position, not scroll) that deliberately contrasts with the
   * hero/manifesto's scroll-scrubbed reveals above.
   *
   * A soft glow follows the cursor anywhere inside the panel via
   * `gsap.quickTo` (a lightweight, GC-free tween that just retargets
   * on every move — the standard way to wire GSAP to a mousemove
   * stream without spinning up a new tween per event). Each QR card /
   * social link sits inside its own `.magnetic-target` wrapper, which
   * eases a few pixels toward the cursor while hovered and springs
   * back out on leave; the wrapper is a separate element from the
   * link itself so this never fights the link's own CSS hover
   * transform (lift, rotate).
   *
   * Fine-pointer + motion gated: on touch devices, or with
   * `prefers-reduced-motion` on, this does nothing and the panel
   * stays the plain static layout with its ordinary CSS `:hover`
   * states — there's no pointer to react to on the former, and no
   * reason to force motion on the latter.
   */
  private setupMagneticSocial(): void {
    const zone = this.magneticZoneRef?.nativeElement;
    const cursor = this.magneticCursorRef?.nativeElement;
    if (!zone || !cursor) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
    if (prefersReducedMotion || !hasFinePointer) {
      return;
    }

    const targets = Array.from(zone.querySelectorAll<HTMLElement>('.magnetic-target'));
    if (!targets.length) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      zone.classList.add('js-magnetic');
      gsap.set(cursor, { opacity: 0, scale: 0.6, xPercent: -50, yPercent: -50 });

      const cursorX = gsap.quickTo(cursor, 'x', { duration: 0.5, ease: 'power3' });
      const cursorY = gsap.quickTo(cursor, 'y', { duration: 0.5, ease: 'power3' });

      // Per-target quickTo setters, keyed by element, plus the
      // strength/cap each one was configured with in the template
      // (a wide social-link row needs a smaller multiplier than the
      // compact QR card so it doesn't drift the label too far).
      const targetState = new Map<
        HTMLElement,
        { x: (v: number) => void; y: (v: number) => void; strength: number; max: number }
      >();

      targets.forEach((el) => {
        targetState.set(el, {
          x: gsap.quickTo(el, 'x', { duration: 0.5, ease: 'elastic.out(1, 0.4)' }),
          y: gsap.quickTo(el, 'y', { duration: 0.5, ease: 'elastic.out(1, 0.4)' }),
          strength: parseFloat(el.dataset['magneticStrength'] ?? '0.35'),
          max: parseFloat(el.dataset['magneticMax'] ?? '18')
        });
      });

      const clamp = (value: number, max: number) => Math.max(-max, Math.min(max, value));

      const onZoneEnter = () => gsap.to(cursor, { opacity: 1, scale: 1, duration: 0.25 });

      const onZoneMove = (event: MouseEvent) => {
        const zoneRect = zone.getBoundingClientRect();
        cursorX(event.clientX - zoneRect.left);
        cursorY(event.clientY - zoneRect.top);
      };

      const onZoneLeave = () => {
        gsap.to(cursor, { opacity: 0, scale: 0.6, duration: 0.25 });
        cursor.classList.remove('magnetic-cursor--hot');
        targetState.forEach((state, el) => {
          state.x(0);
          state.y(0);
          el.classList.remove('magnetic-active');
        });
      };

      zone.addEventListener('mouseenter', onZoneEnter);
      zone.addEventListener('mousemove', onZoneMove);
      zone.addEventListener('mouseleave', onZoneLeave);
      this.magneticCleanupFns.push(() => {
        zone.removeEventListener('mouseenter', onZoneEnter);
        zone.removeEventListener('mousemove', onZoneMove);
        zone.removeEventListener('mouseleave', onZoneLeave);
      });

      targets.forEach((el) => {
        const state = targetState.get(el)!;

        const onEnter = () => {
          el.classList.add('magnetic-active');
          cursor.classList.add('magnetic-cursor--hot');
        };
        const onMove = (event: MouseEvent) => {
          const rect = el.getBoundingClientRect();
          const relX = event.clientX - (rect.left + rect.width / 2);
          const relY = event.clientY - (rect.top + rect.height / 2);
          state.x(clamp(relX * state.strength, state.max));
          state.y(clamp(relY * state.strength, state.max));
        };
        const onLeave = () => {
          el.classList.remove('magnetic-active');
          cursor.classList.remove('magnetic-cursor--hot');
          state.x(0);
          state.y(0);
        };

        el.addEventListener('mouseenter', onEnter);
        el.addEventListener('mousemove', onMove);
        el.addEventListener('mouseleave', onLeave);
        this.magneticCleanupFns.push(() => {
          el.removeEventListener('mouseenter', onEnter);
          el.removeEventListener('mousemove', onMove);
          el.removeEventListener('mouseleave', onLeave);
        });
      });
    });
  }

  /**
   * Called by the newsletter form on successful subscription.
   * If the user came from the "get tickets" flow, redirect them to
   * the payment gateway now that they have subscribed.
   */
  onSubscribed(): void {
    if (this.pendingPaymentRedirect) {
      sessionStorage.removeItem(PENDING_PAYMENT_KEY);
      this.pendingPaymentRedirect = false;

      setTimeout(() => {
        window.location.href = PAYMENT_GATEWAY_URL;
      }, 1200);
    }
  }

  private scrollToFragment(fragment: string, attempts = 0): void {
    const maxAttempts = 20;
    const interval = 80;

    const element = document.getElementById(fragment);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (attempts < maxAttempts) {
      setTimeout(() => this.scrollToFragment(fragment, attempts + 1), interval);
    }
  }

  /**
   * Warm up the Django API on page load.
   * PythonAnywhere free tier puts apps to sleep after inactivity.
   * Throttled to once per browser session — this used to fire on every
   * mount of Home (including every back-navigation to "/"), sending a
   * redundant wake-up ping each time even though the backend, once awake,
   * stays awake for the rest of the session.
   */
  private warmUpDjangoAPI(): void {
    if (sessionStorage.getItem(DJANGO_WARMED_KEY) === 'true') {
      return;
    }
    this.http.get(`${environment.djangoApiUrl}/subscribers/`, {
      headers: { 'X-Device-ID': this.getDeviceId() }
    }).subscribe({
      complete: () => sessionStorage.setItem(DJANGO_WARMED_KEY, 'true')
    });
  }

  private getDeviceId(): string {
    let deviceId = localStorage.getItem('device-id');
    if (!deviceId) {
      deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
      localStorage.setItem('device-id', deviceId);
    }
    return deviceId;
  }
}